export type HelpGuideCategory =
  | 'getting-started'
  | 'data'
  | 'carbon'
  | 'compliance'
  | 'reporting'
  | 'finance'
  | 'enterprise';

export interface HelpFaqItem {
  id: string;
  question: string;
  answer: string;
  category?: HelpGuideCategory | 'general';
}

export interface TrainingGuide {
  id: string;
  title: string;
  category: HelpGuideCategory;
  summary: string;
  /** Primary workspace route for this guide. */
  primaryRoute: string;
  /** Optional CTA button label (defaults to “Open — {title}”). */
  ctaLabel?: string;
  /** Additional routes that should surface this guide in the page banner. */
  relatedRoutes?: string[];
  steps: string[];
  tips?: string[];
}

export const HELP_GUIDE_CATEGORY_LABELS: Record<HelpGuideCategory, string> = {
  'getting-started': 'Getting started',
  data: 'Data & connectors',
  carbon: 'Carbon assessment',
  compliance: 'Compliance hub',
  reporting: 'Reporting & disclosure',
  finance: 'Finance & marketplace',
  enterprise: 'Enterprise program',
};

export const HELP_FAQ_ITEMS: HelpFaqItem[] = [
  {
    id: 'onboarding-path',
    question: 'What is the recommended path after I sign up?',
    answer:
      'Create your account, complete MSME (company) registration, then finish the Operations workflow planner. After that, the dashboard and other modules unlock. If you are sent to Operations or registration, the app is guiding you through those steps.',
    category: 'getting-started',
  },
  {
    id: 'first-30-minutes',
    question: 'What should I do in my first 30 minutes?',
    answer:
      'Sign in, complete company profile and MSME registration, map your operations workflow, upload at least one utility bill or connect Tally/Zoho, then run your first carbon assessment. Review Home for your carbon score and open Compliance to see India or export readiness.',
    category: 'getting-started',
  },
  {
    id: 'workspace-sidebar-order',
    question: 'What order should I follow in the left workspace menu?',
    answer:
      'After onboarding, work top to bottom: My company → Data → Carbon footprint → Compliance (readiness and disclosure reports) → Reduce and offset. Compliance appears before reduction and offsets so you can share filing-ready PDFs before pursuing reductions or credits. The 3-step readiness checklist on Compliance follows the same flow.',
    category: 'getting-started',
  },
  {
    id: 'web-vs-mobile',
    question: 'What is the difference between the web app and the mobile app?',
    answer:
      'The web app includes the full module set (documents, Reduce and offset, government scheme links, forecasting, data privacy, and more). The mobile app focuses on transactions, SMS-assisted capture, carbon analysis, trading, and workflow basics. Use the web app for complete reporting and privacy tools; use mobile for quick operational capture and field review.',
    category: 'general',
  },
  {
    id: 'platform-purpose',
    question: 'What does the Sustainow Carbon Intelligence solution help with?',
    answer:
      'It helps your organization capture activity data, estimate emissions, track reduction actions, and prepare ESG disclosures in a structured workflow.',
    category: 'general',
  },
  {
    id: 'scope-definitions',
    question: 'What are Scope 1, Scope 2, and Scope 3 emissions?',
    answer:
      'Scope 1 covers direct emissions from owned or controlled sources, Scope 2 covers purchased electricity/energy emissions, and Scope 3 covers value-chain emissions such as logistics, procurement, and business travel.',
    category: 'carbon',
  },
  {
    id: 'ghg-accounting',
    question: 'How does the platform support GHG accounting?',
    answer:
      'The solution aligns activity-based inputs with recognized emission-factor logic so teams can compute greenhouse gas totals consistently and review category-level contributions.',
    category: 'carbon',
  },
  {
    id: 'connectors-overview',
    question: 'How do accounting connectors work?',
    answer:
      'Open Data → Connectors to import TallyPrime, Zoho Books, Busy, Marg, Vyapar, or other exports. File import accepts CSV, XLSX, XML, or PDF. Live sync is available for Tally and Zoho when configured. Each connector card shows export steps and required columns.',
    category: 'data',
  },
  {
    id: 'document-upload-ocr',
    question: 'How do bill uploads and OCR affect my carbon score?',
    answer:
      'Upload invoices and utility bills under Upload bills. OCR extracts line items; low-confidence rows are flagged for review. After validation, transactions feed the shared footprint engine. Re-run Carbon Assessment after meaningful data changes to refresh your score.',
    category: 'data',
  },
  {
    id: 'esg-reporting',
    question: 'How is ESG reporting supported?',
    answer:
      'You can organize environmental metrics, supporting documents, and governance-related evidence in one place to improve reporting readiness and internal review quality.',
    category: 'reporting',
  },
  {
    id: 'brsr-support',
    question: 'How does this support BRSR reporting needs?',
    answer:
      'The platform helps structure sustainability data, maintain traceable records, and generate report-ready outputs that can be mapped to BRSR Principle 6 disclosures and assurance workflows. Open Compliance → India track for readiness gates.',
    category: 'compliance',
  },
  {
    id: 'cbam-export',
    question: 'How do I prepare for CBAM or export buyer audits?',
    answer:
      'Use Compliance → Export track for CBAM monitoring, CSRD, CDP, and buyer questionnaire packs. Upload product and site evidence, complete readiness checks, then generate filing-ready exports from Reporting when gates pass.',
    category: 'compliance',
  },
  {
    id: 'report-usage',
    question: 'How should we use generated reports?',
    answer:
      'Use them for management review, vendor and customer disclosures, lender discussions, and periodic sustainability or compliance submissions. They are not a science-based target commitment by themselves.',
    category: 'reporting',
  },
  {
    id: 'reporting-readiness',
    question: 'When are reports filing-ready?',
    answer:
      'Reporting shows readiness gates per framework (BRSR P6, ISO inventories, CBAM, PCF). Complete the checklist items—profile, data coverage, assessment, and evidence—before PDF or statutory exports unlock.',
    category: 'reporting',
  },
  {
    id: 'reporting-benefits',
    question: 'What are the key benefits of regular reporting?',
    answer:
      'Regular reporting improves visibility, supports cost-saving interventions, strengthens stakeholder confidence, and helps you track progress against emissions and ESG goals.',
    category: 'reporting',
  },
  {
    id: 'data-update-frequency',
    question: 'How frequently should we update our data?',
    answer:
      'Monthly updates are recommended for operational metrics. At minimum, ensure every reporting cycle includes complete and validated records before report generation.',
    category: 'data',
  },
  {
    id: 'green-finance',
    question: 'How can carbon data help with government schemes?',
    answer:
      'ADEETIE, MSME ZED, BEE, and PM-KUSUM ask for energy or emissions progress. Open Reduce and offset → Government schemes for the official sites. This workspace does not file applications or work with lenders.',
    category: 'finance',
  },
  {
    id: 'carbon-marketplace',
    question: 'How does the carbon marketplace work?',
    answer:
      'Buy or retire Indian Carbon Market credits against your footprint. Use the wallet to track holdings and retirement history. Offsets complement—but do not replace—operational reduction efforts.',
    category: 'finance',
  },
  {
    id: 'data-privacy',
    question: 'How do I manage data privacy and DPDP requests?',
    answer:
      'Open Data privacy from the main menu to review consent settings and submit access, deletion, or correction requests. Privacy requests are reviewed by the support team within regulatory timelines.',
    category: 'general',
  },
  {
    id: 'contact-support',
    question: 'Who can help us if we are stuck?',
    answer:
      'For onboarding, reporting, or technical support, contact the Sustainow support team at contact@sustainow.in or ask a question from this Help center.',
    category: 'general',
  },
];

export const TRAINING_GUIDES: TrainingGuide[] = [
  {
    id: 'onboard-30min',
    title: 'Your first 30 minutes',
    category: 'getting-started',
    summary: 'Complete registration, data setup, and your first assessment.',
    primaryRoute: '/dashboard',
    relatedRoutes: ['/msme-registration', '/msme-profile', '/operations'],
    steps: [
      'Sign in and open Company profile to confirm legal name, sector, and site details.',
      'Complete MSME registration if prompted—this unlocks carbon models for your organization.',
      'Open Energy and meters and map key process steps to emission-relevant activities.',
      'Go to Data or Upload bills and add at least one utility bill, invoice, or connector export.',
      'Open Carbon footprint, submit activity inputs, and save your first assessment.',
      'Return to Home to review your carbon score and open Compliance for readiness signals.',
    ],
    tips: [
      'The dashboard checklist tracks the same steps—use it as your progress bar.',
      'Incomplete profile or workflow setup is the most common reason modules stay locked.',
    ],
  },
  {
    id: 'workspace-navigation',
    title: 'Workspace menu and readiness order',
    category: 'getting-started',
    summary: 'How the left sidebar is organized and which modules to open first.',
    primaryRoute: '/dashboard',
    relatedRoutes: ['/compliance', '/carbon-footprint', '/data', '/msme-profile'],
    steps: [
      'Start under My company: confirm profile details and map your operations workflow.',
      'Open Data to connect accounting books or upload utility bills and invoices.',
      'Run Carbon footprint to establish your scope breakdown and carbon score.',
      'Open Compliance for the 3-step readiness checklist, India or export tracks, and Disclosure reports when gates pass.',
      'Open Reduce and offset to see savings, set the target, and open official government scheme sites.',
      'Use Offsets & credits after the baseline is in place. Scheme applications are filed on the government sites, not in this workspace.',
    ],
    tips: [
      'New accounts see only Home and My company until every My company page is saved. AI Advisor is the chat button on the screen, not a menu item.',
      'Readiness checklist buttons deep-link to the right page—you do not need to memorize the menu.',
    ],
  },
  {
    id: 'connectors-master',
    title: 'Accounting connectors and imports',
    category: 'data',
    summary: 'Import Tally, Zoho, Busy, Marg, Vyapar, and other books into the footprint engine.',
    primaryRoute: '/data',
    relatedRoutes: ['/document-upload', '/document-management'],
    steps: [
      'Open Data → Connectors and choose your accounting product card.',
      'Read the on-card export steps—each connector lists required columns and sample file hints.',
      'For TallyPrime live sync: enable Tally as server (port 9000) and use Sync now when configured.',
      'For file import: export Day Book or transaction list for your reporting period as CSV, XLSX, or XML.',
      'Upload the file and review imported rows for date, amount, narration, and party name.',
      'Resolve duplicate or failed rows before running Carbon Assessment.',
    ],
    tips: [
      'Align export date ranges with your reporting period before import.',
      'Party and ledger names improve category mapping for scope allocation.',
    ],
  },
  {
    id: 'docs-ocr',
    title: 'Bills, OCR, and data quality',
    category: 'data',
    summary: 'Upload evidence, review OCR output, and keep calculations traceable.',
    primaryRoute: '/document-upload',
    relatedRoutes: ['/data', '/document-management'],
    steps: [
      'Open Upload bills and attach utility bills, fuel invoices, or receipts (PDF, image, or spreadsheet).',
      'Wait for processing—status moves from queued to extracted or needs review.',
      'Open the document detail to correct low-confidence OCR fields before they affect totals.',
      'Check duplicate and spam queues so the same bill is not counted twice.',
      'After validation, confirm new transactions appear under Data.',
      'Re-run Carbon Assessment when document-backed activity changes materially.',
    ],
    tips: [
      'Clear scans and standard invoice layouts improve OCR accuracy.',
      'Geotagged site photos (Compliance) strengthen audit evidence for site visits.',
    ],
  },
  {
    id: 'assessment-run',
    title: 'Running a carbon assessment',
    category: 'carbon',
    summary: 'Establish or refresh your emissions baseline from operational data.',
    primaryRoute: '/carbon-footprint',
    relatedRoutes: ['/carbon-forecasting', '/carbon-savings', '/emissions-reduction-targets'],
    steps: [
      'Open Carbon footprint and choose Start assessment or refresh an existing period.',
      'Confirm operational inputs—energy, fuels, materials, and transport—match your latest data.',
      'Review scope breakdown and category contributions before saving.',
      'Open Compliance for readiness checks and disclosure report exports.',
      'Open Recommendations to see reduction actions ranked by impact where data allows.',
      'Set Target from your baseline for monthly or annual tracking.',
      'Use Forecasting to explore trend scenarios after you have two or more assessments.',
    ],
    tips: [
      'Carbon score updates only after a successful assessment run—not from uploads alone.',
      'Hover glossary tooltips on the dashboard for plain-language scope definitions.',
    ],
  },
  {
    id: 'compliance-india',
    title: 'India compliance track',
    category: 'compliance',
    summary: 'BRSR Principle 6, PAT-style intensity, SBTi, ISO 14064, and ICM alignment.',
    primaryRoute: '/compliance/india',
    relatedRoutes: ['/compliance', '/buyer-audit'],
    steps: [
      'Open Compliance and select the India market track.',
      'Complete the onboarding checklist: profile, workflow, data coverage, and assessment.',
      'Review BRSR Principle 6 readiness gates and missing evidence items.',
      'Upload supporting documents linked to each disclosure theme where prompted.',
      'Track PAT-style intensity and SBTi target progress from the same hub.',
      'When readiness passes, open Reporting for BRSR P6 and ISO inventory exports.',
    ],
    tips: [
      'Dismiss the onboarding alert only after you understand the three setup steps—it links here.',
      'Buyer audit pack consolidates supplier-pack requests in one place.',
    ],
  },
  {
    id: 'compliance-export',
    title: 'Export and CBAM readiness',
    category: 'compliance',
    summary: 'CBAM, CSRD, CDP, TCFD, and buyer questionnaire preparation.',
    primaryRoute: '/compliance/export',
    relatedRoutes: ['/compliance/product-pcf', '/compliance/geotagged-capture'],
    steps: [
      'Open Compliance → Export track for international customer requirements.',
      'Identify which frameworks apply (CBAM, CSRD, CDP, EcoVadis, EUDR).',
      'Complete product PCF workspace inputs if buyers request product carbon footprints.',
      'Capture geotagged site evidence where site visits or chain-of-custody proof is required.',
      'Resolve export-track readiness gates before generating registry-style files.',
      'Share outputs from Reporting or buyer audit packs with your export customer.',
    ],
    tips: [
      'Export and India tracks share the same underlying inventory—keep data current once.',
      'Start CBAM monitoring early in the calendar year to avoid quarter-end gaps.',
    ],
  },
  {
    id: 'reporting-ready',
    title: 'Disclosure reports and filing readiness',
    category: 'reporting',
    summary: 'Generate BRSR, ISO, CBAM, and PCF packs when gates pass.',
    primaryRoute: '/reporting',
    steps: [
      'Open Reporting and choose your disclosure intent (buyer, bank, export, internal).',
      'Review readiness gates for each framework—red items block filing-ready exports.',
      'Use simple mode for guided PDFs or expert mode for statutory JSON/XBRL where enabled.',
      'Select the reporting period aligned with your latest saved assessment.',
      'Preview the report summary before download or external submission.',
      'Store generated PDFs and attach them to lender or buyer workflows as needed.',
    ],
    tips: [
      'The reporting intent wizard tailors labels to your audience—set it once per cycle.',
      'Re-run assessment after connector imports so period totals stay consistent.',
    ],
  },
  {
    id: 'finance-gift',
    title: 'Government schemes for energy and MSME support',
    category: 'finance',
    summary: 'Use your reduction target as evidence, then apply on the official government site.',
    primaryRoute: '/carbon-savings?section=schemes',
    relatedRoutes: ['/emissions-reduction-targets', '/carbon-footprint'],
    steps: [
      'Open Reduce and offset → Target and set the reduction you want from your recent monthly emissions.',
      'Check This year to see whether recorded emissions are inside that budget.',
      'Open Government schemes for ADEETIE, MSME ZED, BEE, PM-KUSUM, and the MSME scheme list.',
      'Apply on the official site. This workspace does not submit applications or work with lenders.',
      'Keep utility bills and completed reduction ideas ready. Portals often ask for that evidence.',
    ],
    tips: [
      'Keep utility and equipment invoices uploaded — schemes often require documentary proof.',
      'Application status stays on the government portal.',
    ],
  },
  {
    id: 'marketplace-offsets',
    title: 'Carbon marketplace and credit retirement',
    category: 'finance',
    summary: 'Buy, hold, and retire Indian Carbon Market credits.',
    primaryRoute: '/carbon-savings?section=trading',
    relatedRoutes: ['/carbon-credits', '/carbon-trading'],
    steps: [
      'Open the Indian Carbon Market Marketplace from the main menu.',
      'Review available offset projects and wallet balance.',
      'Purchase credits aligned with your retirement policy and budget.',
      'Retire credits against a specific reporting period to document offset claims.',
      'Download retirement records for disclosure appendices where required.',
      'Prioritize operational reductions; offsets should supplement measurable cuts.',
    ],
  },
  {
    id: 'enterprise-program',
    title: 'Enterprise program setup',
    category: 'enterprise',
    summary: 'Portfolio reporting, supplier data, and enterprise compliance.',
    primaryRoute: '/enterprise',
    ctaLabel: 'Open Enterprise program',
    relatedRoutes: [
      '/enterprise/dashboard',
      '/enterprise/profile',
      '/enterprise/workflow',
      '/enterprise/data',
      '/enterprise/carbon-footprint',
      '/enterprise/compliance',
      '/enterprise/reporting',
    ],
    steps: [
      'Complete enterprise registration with legal entity and site hierarchy.',
      'Map enterprise workflow boundaries before rolling up supplier inventories.',
      'Import supplier documents and connector feeds under Enterprise Data.',
      'Run enterprise carbon inventory across sites and material scope 3 categories.',
      'Use Enterprise Compliance for BRSR and India-market supplier evidence trails.',
      'Generate enterprise disclosure PDFs from Enterprise Reporting.',
    ],
    tips: [
      'Enterprise and MSME workspaces are separate—use the correct portal login.',
      'Help center training guides also apply to enterprise routes where modules match.',
    ],
  },
];

export const USER_MANUAL_STEPS = [
  {
    title: '1) Sign in and complete your profile',
    description:
      'Log in with your registered account, open Company profile, and complete MSME registration so carbon models use accurate sector, site, and boundary details.',
    route: '/msme-profile',
  },
  {
    title: '2) Map operations in the workflow planner',
    description:
      'Open Energy and meters and link process steps to emission drivers. This unlocks the full module set and improves category allocation.',
    route: '/operations',
  },
  {
    title: '3) Import data and upload documents',
    description:
      'Use Data connectors (Tally, Zoho, Busy, and others) or Upload bills for bills and invoices. Review OCR output and duplicates before assessments.',
    route: '/data',
  },
  {
    title: '4) Run and refresh carbon assessments',
    description:
      'Open Carbon footprint, submit activity inputs for the period, and save the assessment. Re-run after major data changes to update your carbon score.',
    route: '/carbon-footprint',
  },
  {
    title: '5) Prepare compliance and generate reports',
    description:
      'Open Compliance for the 3-step readiness checklist, India and export tracks, and Disclosure reports for BRSR, ISO, CBAM, and PCF PDF exports when gates pass.',
    route: '/compliance',
  },
  {
    title: '6) Reduce and offset',
    description:
      'See measured savings, set the target, open government scheme sites, then review offset projects and the credits wallet.',
    route: '/carbon-savings?section=savings',
  },
  {
    title: '7) Open government scheme sites',
    description:
      'From Government schemes, open ADEETIE, MSME ZED, and other official programmes. Applications are filed on those sites.',
    route: '/carbon-savings?section=schemes',
  },
  {
    title: '8) Offsets and credits',
    description:
      'On Reduce and offset, open Offsets and credits to review projects, manage the wallet, and retire credits when offsets fit your policy.',
    route: '/carbon-savings?section=trading',
  },
];

export const TROUBLESHOOTING_FAQ_ITEMS: HelpFaqItem[] = [
  {
    id: 'login-issues',
    question: 'Why can I not log in to the platform?',
    answer:
      'Confirm your email/password, check internet connectivity, and use the Forgot Password option if needed. Ensure you are on the correct portal (MSME, Enterprise, Partner, or Admin). If the issue persists, contact support.',
  },
  {
    id: 'empty-dashboard',
    question: 'Why is my dashboard showing incomplete or empty data?',
    answer:
      'If Home already shows a footprint total, open Carbon Footprint → Overview or Analytics for the same assessment numbers — module views can look empty until that tab finishes loading. When Home is also blank, finish onboarding, upload bills, run operations workflow, and submit at least one assessment.',
  },
  {
    id: 'missing-documents',
    question: 'Why are my uploaded files not visible in Document Management?',
    answer:
      'Wait a few moments for processing to complete, then refresh. Ensure file format and size are supported and upload succeeded. Check the spam or duplicate queue.',
  },
  {
    id: 'score-not-updating',
    question: 'Why is carbon score not changing after I updated data?',
    answer:
      'Scores update after a successful assessment run. Re-submit the latest data and confirm there were no validation errors on imported transactions or OCR rows.',
  },
  {
    id: 'report-failure',
    question: 'What should I do if report generation fails?',
    answer:
      'Retry after a short interval and verify readiness gates (profile, data, assessment, evidence). If failures continue, report the issue with exact steps to support.',
  },
  {
    id: 'connector-import-failed',
    question: 'Why did my accounting import fail or show zero rows?',
    answer:
      'Confirm the export includes date, amount, and narration columns. Match the date range to your reporting period and use the sample format shown on the connector card.',
  },
  {
    id: 'compliance-gates-blocked',
    question: 'Why are compliance or reporting exports still locked?',
    answer:
      'Readiness gates list missing items—commonly incomplete workflow, no saved assessment, or absent evidence documents. Resolve each red gate in Compliance or Reporting.',
  },
  {
    id: 'support-contact',
    question: 'How can I contact support for reporting or ESG guidance?',
    answer:
      'For platform, reporting, or ESG workflow support, write to contact@sustainow.in with your organization name and issue details, or use Ask a question on the Help center.',
  },
];

const normalizePath = (pathname: string) => {
  let path = (pathname || '/').split('?')[0].split('#')[0];
  if (path.length > 1 && path.endsWith('/')) {
    return path.slice(0, -1);
  }
  return path;
};

const pathMatchesGuide = (pathname: string, guide: TrainingGuide): boolean => {
  const path = normalizePath(pathname);
  if (path === guide.primaryRoute || path.startsWith(`${guide.primaryRoute}/`)) {
    return true;
  }
  return (guide.relatedRoutes || []).some(
    (route) => path === route || path.startsWith(`${route}/`)
  );
};

/** Best matching training guide for the current route (primary route beats related routes). */
export function resolveTrainingGuideForPath(pathname: string): TrainingGuide | null {
  const path = normalizePath(pathname);
  let best: TrainingGuide | null = null;
  let bestScore = -1;

  TRAINING_GUIDES.forEach((guide) => {
    if (!pathMatchesGuide(path, guide)) {
      return;
    }

    const primaryMatches =
      path === guide.primaryRoute || path.startsWith(`${guide.primaryRoute}/`);
    const relatedMatches = (guide.relatedRoutes || []).filter(
      (route) => path === route || path.startsWith(`${route}/`)
    );

    let score = 0;
    if (primaryMatches) {
      score = 1000 + guide.primaryRoute.length;
    } else if (relatedMatches.length > 0) {
      score = Math.max(...relatedMatches.map((route) => route.length));
    }

    if (score > bestScore) {
      best = guide;
      bestScore = score;
    }
  });

  return best;
}

export function buildHelpGuideUrl(guideId: string): string {
  return `/help#guide-${guideId}`;
}

export function getTrainingGuidesByCategory(): Array<{
  category: HelpGuideCategory;
  label: string;
  guides: TrainingGuide[];
}> {
  const order: HelpGuideCategory[] = [
    'getting-started',
    'data',
    'carbon',
    'compliance',
    'reporting',
    'finance',
    'enterprise',
  ];

  return order.map((category) => ({
    category,
    label: HELP_GUIDE_CATEGORY_LABELS[category],
    guides: TRAINING_GUIDES.filter((guide) => guide.category === category),
  }));
}
